import { IStringIndexedObject } from '../../utils/utils'
import { ITradeOptionWithProfit } from '../rtkApi/tradeOptionsApi'
import { ETransferDirection } from '../rtkApi/subAccountApi'

export enum EManualActions {
  order,
  withdraw,
  transfer,
}

export enum ETradeOptionStatus {
  green = 'green',
  yellow = 'yellow',
  black = 'black',
  all = 'all',
}

export type TradeOption = {
  _id?: string
  market: string
  secondMarket?: string
  firstEx: string
  secondEx: string
  status?: number
  buyFee?: number
  sellFee?: number
  minVolume?: number
  marketBuy?: number
  marketSell?: number
  limitBuy?: number
  limitSell?: number
  isEnabled?: boolean
  marketStep?: number
  limitStep?: number
  isAutoLimitEnabled?: boolean
  firstBid?: { price: number; volume: number }
  firstAsk?: { price: number; volume: number }
  secondBid?: { price: number; volume: number }
  secondAsk?: { price: number; volume: number }
  firstExPing?: number
  secondExPing?: number
  firstExBuyLowVol?: boolean
  firstExSellLowVol?: boolean
  secondExBuyLowVol?: boolean
  secondExSellLowVol?: boolean
  isFreezed?: boolean
  marketSituationTime?: number
  lowBalance?: boolean
  isDeleted?: boolean
  isFastEnable?: boolean
  isFastLimitEnable?: boolean
  isBMEnabled?: boolean
  isFastBMEnable?: boolean
  lastLimitDecisionTime?: number
  lastUpdateTime?: number
  lastProfit?: number
  colorChangesByWeek?: IColorChangesByWeek
  tradeEnabled?: boolean
}
export interface IColorChanges {
  greenCounter: number
  yellowCounter: number
  blackCounter: number
  greenDuration: number
  yellowDuration: number
  blackDuration: number
  lastChangeTime: number
  greenCount?: number
  yellowCount?: number
}

export interface IColorChangesByWeek {
  [key: number | string]: IColorChanges

  0: IColorChanges
  1: IColorChanges
  2: IColorChanges
  3: IColorChanges
  4: IColorChanges
  5: IColorChanges
  6: IColorChanges
}

export interface ITradeOptionSearchTab extends TradeOption {
  marketInfo?: {
    exchange: string
    market: string
    volume24: number
    change24: string
  }[]
  volume24Sum?: number
  colorStatus: number
  maxChange24: number
}

export interface ITradeOptionTradesTab extends TradeOption {
  colorStatus: number
  lastProfit: number
  ordersTotal?: number
  profit?: number
  actionsCount?: number
  calcProfits?: {
    tradeOptionId: string
    weekProfit: number
    monthProfit: number
  }[]
}

export interface ITradeOptionProps {
  isInAction?: boolean
  tradeOption: ITradeOptionWithProfit
  isActive?: boolean
  isPinned?: boolean
  scrollOnChoose?: boolean
  index?: number
  backsValue?: number
}

export interface ITradeOptInterface {
  firstEx: string
  secondEx: string
  market: string
  secondMarket?: string
  _id?: string
  tradeOptionId?: string
  isEnabled?: boolean
  isAutoLimitEnabled?: boolean
  limitBuy?: number
  limitSell?: number
  limitStep?: number
  marketBuy?: number
  marketSell?: number
  marketStep?: number
  firstExPing?: number
  secondExPing?: number
  priceLines?: number
  isFreezed?: boolean
  isFastEnable?: boolean
  isFastLimitEnable?: boolean

  isBMEnabled?: boolean
  isFastBMEnable?: boolean

  volumeLimits?: {
    firstEx: {
      firstSymbol: number
      secondSymbol: number
    }
    secondEx: {
      firstSymbol: number
      secondSymbol: number
    }
  }
  areLogicsTogether?: boolean
  limitLogicType?: 'SINGLE' | 'MULTI'
  myTrades?: {
    firstEx: IPrivateTrades[]
    secondEx: IPrivateTrades[]
  }
  tradeEnabled?: boolean
  dynamicProfitStepEnabled?: boolean
  dynamicProfitStepLowBorder?: number
  dynamicProfitStepHighBorder?: number
}

export type TotalBalance = {
  symbol: string
  value: number
}

export interface Balance {
  _id: string
  exchange: string
  symbol: string
  available: number
  onOrder: number
  total: number
  date: number
  reserve: number
  accountName: string
}

export interface Depth {
  exchange: string
  market: string
  bids: { price: number; volume: number }[]
  asks: { price: number; volume: number }[]
  date: Date
}

export type Withdraw = {
  _id: string
  withdrawId: string
  amount: number
  date: Date
  exchange: string
  fee: number
  status: string
  symbol: string
  txId: string
  checked: boolean
}
export type Deposit = {
  _id: string
  depositId: string
  amount: number
  date: Date
  exchange: string
  fee: number
  status: string
  symbol: string
  txId: string
  checked: boolean
}

export interface Trade {
  tradeId: string
  side: string
  maker: boolean
  price: number
  volume: number
  time: number
  fee: number
  feeAsset: string | null
  calculatedFee?: number
}

export interface TradeHistory extends Trade {
  exchange: string
  market: string
  placedBy: string
}

export type Order = {
  _id: string
  orderId: string
  checked: boolean
  date: number
  exchange: string
  filled: number
  isActive: boolean
  market: string
  orderStatus: string
  orderType: string
  placedBy: string
  price: number
  avgPrice?: number
  side: string
  volume: number
  isPending?: boolean
  customId?: string
  error?: string
  actionId?: string
  tradeOptionId?: string
  isAutoLimit?: boolean
  createdAt?: number
  updatedAt?: number
  firstResponseTime?: number
  trades?: Trade[]
  secondActionPrice?: number
  accountName?: string
  isIOK?: boolean
  IOC?: boolean
  createdTime?: number
  createdInSocketTime?: number
  gotOrderIdTime?: number
  deactivatedTime?: number
}

export type Action = {
  _id: string
  firstSide: string
  tradeOptionId: string
  filledAmount: number
  firstStatus: string
  firstOrder: string
  firstDate: number
  firstFee?: number
  firstFeePercent?: number
  secondStatus: string
  secondOrders: string[]
  secondDate: number
  secondFee?: number
  secondFeePercent?: number
  expectedFirstBalance: number
  expectedSecondBalance: number
  createdAt: number
  finishedAt?: number
  profit: number
  actionOrders?: Order[]
  firstPrice?: number
  secondPrice?: number
  amount?: number
  tradeOptionInfo: [TradeOption]
  logicType: string
  expectedProfit: number
  expectedProfitPercent?: number
  swapExchanges?: boolean
  decisionTime?: number
  depthTime?: number
  handleGoToTradeOption?: (trOpt: TradeOption) => void
  filterByClickedAction?: (props: { firstExchange: string; secondExchange: string; market: string; secondMarket: string | undefined }) => void
  allOrders?: Order[]
  securedPrice?: number
  feeSum?: number
}

export type BotsList = {
  _id: string
  name: string
  exchanges: {
    name: string
    isEnabled: boolean
    isFreezed: boolean
    isEnabledInBot: boolean
  }[]
}

export type MarketsBotsList = {
  _id: string
  market: string
  isEnabledInBot: boolean
  isFreezed: boolean
}

export enum ELittleBotTabs {
  TradeOptions = 'tradeOptions',
  Actions = 'actions',
  Balances = 'balances',
  Orders = 'orders',
  WithdrawsDeposits = 'withdrawsDeposits',
  Exchanges = 'exchanges',
}

type exchangesType = 'Binance' | 'Huobi' | 'Poloniex' | 'Kucoin' | 'ByBit' | 'Pancake' | 'JustSwap' | 'JustSwapV2' | 'Kuna' | 'WhiteBit'

interface ICropBalances {
  total: number
  exchange: exchangesType
  limit?: number
  perfect?: number
}

export interface IAllBalancesResponse {
  total: number
  symbol: string
  totalLimit?: number | undefined
  fiatTotal: number
  exchanges: string[]
}

export interface IBotsState {
  name: string
  tabs: {
    tab: ELittleBotTabs
  }
  deletingTradeOption: {
    state: boolean
    tradeOption: Pick<TradeOption, 'firstEx' | 'secondEx' | 'market' | 'secondMarket' | '_id'> | undefined
  }
  withdrawDataFromBalancesTab: {
    from: string
    symbol: string
    to: string
  }

  isTradeOptionsHistoryPaused: boolean
  tradeOptionsHistory: TradeOption[]
  totalBalances: TotalBalance[]
  selectedToBalances: Balance[]
  depths: Depth[]
  ordersHistory: { totalPages: number; orders: Order[] }
  placeOrderLoading: boolean
  placeOrderError: string
  withdrawLoading: boolean
  withdrawError: string
  depthsLoading: boolean

  dexFeeMarketExists: {
    loading: boolean
    exists: boolean
  }
  firstActionsStatuses: {
    new: number
    done: number
    error: number
  }
  secondActionsStatuses: {
    new: number
    done: number
    error: number
  }
  settingsTab: {
    exchangesList: {
      loading: boolean
      data: Exchange[]
    }
    exchangeMarketsList: {
      loading: boolean
      data: Market[]
      pages: number
    }
    botsListSettings: BotsList[]
    marketsBotsListSettings: { markets: MarketsBotsList[]; pages: number }
    marketsBotsListSettingsLoading: boolean
  }
  trades: {
    firstExTrades: ITrade[]
    secondExTrades: ITrade[]
  }
  tradeOptionsDepthsOptions: {
    loading: boolean
    firstEx: {
      symbolFullName?: string
      pricePrecision: number
      volumePrecision: number
      volume24: number | string
      change24: number | string
      isActualMarket: boolean
    }
    secondEx: {
      symbolFullName?: string
      pricePrecision: number
      volumePrecision: number
      volume24: number | string
      change24: number | string
      isActualMarket: boolean
    }
  }
  infoPopup: InfoPopup

  inputsData: {
    withdrawForm: {
      chains: {
        firstExLoading: boolean
        firstEx: {
          chainNames: string[]
          chains: IChain[]
        }
        secondExLoading: boolean
        secondEx: {
          chainNames: string[]
          chains: IChain[]
        }
      }
      verifiedLoading: boolean
      verified: {
        verifiedNames: string[]
        verified: {
          _id: string
          exchangeFrom: string
          exchangeTo: string
          symbolFrom: string
          symbolTo: string
          chainFrom: string
          chainTo: string
          spareChainName: string
          address: string
          memo: string | undefined
          allowedForBalanceManager?: boolean
          feeRatio?: number
          sendRatio?: number
        }[]
      }
      isDraftOpened: boolean
      draft: undefined | IWithdrawDraft
    }
  }
  token: string | undefined

  lowBalances: {
    firstEx: string
    secondEx: string
    market: string
    secondMarket: string
  }[]

  isEnabledActive: boolean
  selectedTradeOption: ITradeOptionWithScroll
  placeOrderManualValues: {
    manualPrice?: { exchange: string; market: string; price: string; accountName: string }
    manualVolume?: { exchange: string; market: string; volume: string; accountName: string }
    manualTotal?: { exchange: string; market: string; total: string; accountName: string }
  }
  transferManualValues: {
    manualExchange: string
    manualSymbol: string
    manualAccountName: string
    manualDirection: string
  }
  manualActionsTab: EManualActions
  formMenuOpened: boolean
  actionsFilterByExchange: {
    firstEx?: string
    secondEx?: string
    market?: string
    secondMarket?: string
  }
  tradeFirstExType?: string
  tradeSecondExType?: string
  toFiltersByExchange?: string
  toFiltersByMarket?: string
  toFiltersBySymbol?: string
  toFiltersByStatus?: ETradeOptionStatus
  searchTabState?: SearchTabState
  openedTradeOption: boolean
  searchSortState: string[]
  isActionOpen: boolean
  recordsTabOpened: boolean
  selectedTimelineDot: number
  isHideRecorderTOList: boolean
}

export type SearchTabState = {
  symbol?: string
  market?: string
  exchange?: string
  lowBalance?: boolean
  toStatusGreen?: boolean
  toStatusYellow?: boolean
  toStatusBlack?: boolean
  toFirstEx?: string
  toSecondEx?: string
  toFirstMarket?: string
  toSecondMarket?: string
  sort?: string[]
  page?: number
  ping?: boolean
}

type InfoPopup = {
  isShow: boolean
  autoClose: boolean | number
  status: {
    success: string
    error: string
    warning: string
  }
}

export interface Exchange {
  _id: string
  name: string
  volume24: number
  exMarkets: {
    enabled: number
    disabled: number
    freezed: number
  }
  isEnabled: boolean
  isFreezed: boolean
  accounts: { id: number; name: string; bot: string }
  fee: number
  limitFee: number
  exType: string
  feeSymbol?: string
  feeSymbolConfig?: {
    exchange: string
    perSubAccountLimit: number
    symbol: string
  }
}

export interface Market {
  _id: string
  market: string
  exchange: string
  isActive: boolean
  isFreezed: boolean
  minTotal: number
  minVolume: number
  pricePrecision: number
  spread: number
  volumePrecision: number
  change24: string
  lastPrice: number
  volume24: number
  addressFirstSymbol: string
  addressSecondSymbol: string
  addressPair: string
  addressSecondPair: string
  qtySteps: number
  startVolume: number
  step: number
  marketFee?: number
  limitFee?: number
}

export interface IGetTradeOptionsParams extends IStringIndexedObject<string[] | number | boolean | string | undefined> {
  pinnedIDs?: string[]
  page?: number
  pageSize?: number
  isAutoEnabled?: boolean
  searchValue?: string
  isDeleted?: boolean
  onlyPinned?: boolean
}

export interface ITradeOptionsStat {
  total?: number
  limit?: number
  market?: number
  simple?: number
  maxPage?: number
  wasFiltered?: boolean
}

export interface IDepthRow {
  price: number
  volume: number
  totalVolume: number
}

export interface IDepth {
  exchange: string
  market: string
  date: Date
  asks: IDepthRow[]
  bids: IDepthRow[]
}

export interface ITrade {
  exchangeName: string
  tradeId: string
  market: string
  time: number
  price: number
  volume: number
  type: string
}

export interface IPrivateTrades {
  side: string
  maker: boolean
  price: number
  volume: number
  time: number
  fee: number
  feeAsset: string | null
  tradeId?: number | string
}

export interface IPrivateTrade {
  firstExchange: Trade[]
  secondExchange: Trade[]
}

export interface IChain {
  network: string
  exchangeNetworkName: string
  withdrawFee?: string
  withdrawMin: string
  mainAddress?: string
  isMemo?: boolean
}

export interface IWithdrawDraft {
  tag: string | undefined
  amount: number
  chainTo: string
  address: string
  symbolTo: string
  verified: boolean
  chainFrom: string
  exchangeTo: string
  symbolFrom: string
  exchangeFrom: string
  isDeposit: boolean
  withdrawFee: number
  minAvailableWithdraw: number
}

export interface IWithdrawSchema {
  date?: number
  txId?: string
  memo?: string
  status?: string
  failed?: boolean
  address?: string
  feeAmount?: number
  amount: number
  chainTo: string
  symbolTo: string
  chainFrom: string
  withdrawId: string | number
  symbolFrom: string
  isDeposit: boolean
  exchangeTo: string
  exchangeFrom: string
  verified: boolean
  confirmed: boolean
  uniqueWithdrawId?: string
}

export interface ITradeOptionWithScroll extends ITradeOptInterface {
  scrollToTradeOption?: boolean
  filteredTO?: string
}

export interface ITradeOptionSearchTabProps {
  isInAction?: boolean
  tradeOption: ITradeOptionSearchTab
  isActive?: boolean
  isPinned?: boolean
  scrollOnChoose?: boolean
  index: number
  firstEx24Volume?: number
  secondEx24Volume?: number
}

export interface ITradeOptionTradesTabProps {
  tradeOption: ITradeOptionTradesTab
  isPinned?: boolean
  toValueLogicUpdate?: boolean
  index: number
}

export type LocalStorageSearchTabFilters = {
  symbol?: string
  market?: string
  exchange?: string
  lowBalance?: boolean
  toStatusGreen?: boolean
  toStatusYellow?: boolean
  toStatusBlack?: boolean
  toFirstEx?: string
  toSecondEx?: string
  toFirstMarket?: string
  toSecondMarket?: string
  page: number
  ping: boolean
  limitEnabled?: boolean
  autoLimit?: boolean
}

export interface IVolTronDetailsArgs {
  exchange: string
  market: string
  second_exchange?: string
  id?: string
  first_sub_acc?: string
  second_ex_sub_acc?: string
  inventory_amount?: number
}

export interface LocalStoragePaperActionsTabFilters {
  symbol?: string
  market?: string
  exchange?: string
  page: number
  sorts: string[]
  searchValue: string
  showAll: boolean
  last24h: boolean
  lastHour: boolean
  startTime?: number
  endTime?: number
  timeIndex: number
}
